import * as Sentry from "@sentry/react";

// =============================================================================
// Sentry error reporting (browser)
//
// Observability wiring only — additive, never load-bearing. Same two hard
// rules as the API side (see apps/api/src/lib/sentry.ts):
//
//   1. **No-op without a DSN.** The DSN comes from `VITE_SENTRY_DSN`, baked
//      in at build time. When it's empty/unset (local dev and any build
//      that wasn't given the var), `initSentry()` returns `false` and the
//      SDK never initializes — the app boots and runs exactly as before.
//
//   2. **PHI never leaves the building.** This portal renders patient names,
//      MRN, DOB, and addresses on screen, so we are aggressively
//      conservative: `sendDefaultPii` is off, Session Replay and Tracing
//      are NOT enabled (a replay would literally record PHI; spans carry
//      URLs + query strings), console breadcrumbs are dropped, breadcrumb
//      `data` is stripped, and a `beforeSend` pass
//      trims each event to the error message + stack + a non-PHI request
//      path. When in doubt we send less.
// =============================================================================

let initialized = false;

/**
 * Initialize Sentry if (and only if) `VITE_SENTRY_DSN` is set. Idempotent;
 * returns whether Sentry is actually live. Call this once, as early as
 * possible, from the entry file (src/main.tsx).
 */
export function initSentry(): boolean {
  if (initialized) return true;

  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim();
  if (!dsn) {
    // No DSN → stay completely dark. `Sentry.captureException` remains a
    // safe no-op because init never ran.
    return false;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // Same semver the footer + "What's new" dialog show, injected by Vite's
    // `define`. Lets Sentry group regressions by release.
    release: typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : undefined,

    // PHI guard rails ------------------------------------------------------
    sendDefaultPii: false,
    // No tracing, no replay — both are PHI exfiltration surfaces we don't
    // need for "an error happened, here's the stack." (Hence no
    // browserTracing / replay integrations below.)

    // Console breadcrumbs are dropped in `scrubBreadcrumb` (by category)
    // rather than by removing an integration: in @sentry/browser v10 console
    // capture lives inside the default Breadcrumbs integration, so there is no
    // separate "Console" integration to filter out (the old filter here was a
    // no-op). Dropping by category is version-proof and guarantees app/library
    // console output — which could carry patient context — never rides along
    // on an event.
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend: scrubEvent,
  });

  initialized = true;
  return true;
}

// =============================================================================
// Scrubbing — mirrors the server-side policy in apps/api/src/lib/sentry.ts
// =============================================================================

/** Strip the query string and fragment from a URL, keeping just the path.
 *  Query strings are a classic PHI carrier (e.g. `?mrn=...`). */
function stripUrl(url: string): string {
  const queryIdx = url.indexOf("?");
  const hashIdx = url.indexOf("#");
  const cut = [queryIdx, hashIdx].filter((i) => i >= 0).sort((a, b) => a - b)[0];
  return cut === undefined ? url : url.slice(0, cut);
}

/** Reduce an event to the non-PHI essentials: keep the request method +
 *  path, drop body/query/headers/cookies, and trim the user to a bare id. */
function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  if (event.request) {
    event.request = {
      ...(event.request.method ? { method: event.request.method } : {}),
      ...(event.request.url ? { url: stripUrl(event.request.url) } : {}),
    };
  }

  if (event.user) {
    event.user = event.user.id ? { id: String(event.user.id) } : {};
  }

  return event;
}

/** Strip `data` (URLs / query strings live here) from every breadcrumb and
 *  sanitize any URL-ish message, so the trail can't smuggle PHI out. */
function scrubBreadcrumb(
  breadcrumb: Sentry.Breadcrumb,
): Sentry.Breadcrumb | null {
  // Drop console breadcrumbs entirely — app/library console output could carry
  // patient context, and we never want that stream attached to an event.
  if (breadcrumb.category === "console") {
    return null;
  }
  if (breadcrumb.data) {
    delete breadcrumb.data;
  }
  if (typeof breadcrumb.message === "string" && breadcrumb.message.includes("?")) {
    breadcrumb.message = stripUrl(breadcrumb.message);
  }
  return breadcrumb;
}

export { Sentry };
